import React, { useState, useEffect } from 'react';
import './AdminJobListing.css';
import Dashboard from './Dashboard.js';
import { useNavigate } from 'react-router';

const AdminJobListing = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0);
  
  // Enhanced date filter state
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    display: 'Jul 19 - Jul 25'
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); 
  const [filters, setFilters] = useState({
    status: [],
    jobType: [],
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  
  const navigate = useNavigate();
  
  const fetchJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
      });

      // Add filters to params
      if (filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters.jobType.length > 0) {
        params.append('job_type', filters.jobType.join(','));
      }
      if (filters.search.trim()) {
        params.append('query', filters.search.trim());
      }
      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('date_to', filters.dateTo);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs/list-jobs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.items || []);
        setTotalJobs(data.total || 0);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch jobs');
      }
    } catch (error) {
      setError('Failed to fetch jobs');
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [currentPage, pageSize, filters]);

  const handleFilterChange = (filterType, value, checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterType === 'search') {
        newFilters.search = value;
      } else {
        if (checked) {
          newFilters[filterType] = [...prev[filterType], value];
        } else {
          newFilters[filterType] = prev[filterType].filter(item => item !== value);
        }
      }
      
      return newFilters;
    });
    
    setCurrentPage(1);
  };

  // Date filter handlers
  const handleDateRangeChange = (startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: startDate,
      dateTo: endDate
    }));
    
    // Update display
    if (startDate && endDate) {
      const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      setDateRange({
        startDate,
        endDate,
        display: `${start} - ${end}`
      });
    }
    
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  // Predefined date ranges
  const applyDateRange = (range) => {
    const today = new Date();
    let startDate, endDate;
    
    switch (range) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7days':
        endDate = today.toISOString().split('T')[0];
        startDate = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        break;
      case 'last30days':
        endDate = new Date().toISOString().split('T')[0];
        startDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0];
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    handleDateRangeChange(startDate, endDate);
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      jobType: [],
      search: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setDateRange({
      startDate: '',
      endDate: '',
      display: 'Select Date Range'
    });
    setCurrentPage(1);
  };

  const getActiveFilterCount = () => {
    return filters.status.length + 
           filters.jobType.length + 
           (filters.search ? 1 : 0) + 
           (filters.dateFrom && filters.dateTo ? 1 : 0);
  };

  // Enhanced badge functions
  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'live':
        return 'status-badge status-active';
      case 'closed':
        return 'status-badge status-closed';
      case 'draft':
        return 'status-badge status-draft';
      case 'paused':
        return 'status-badge status-paused';
      default:
        return 'status-badge status-default';
    }
  };

  const getJobTypeBadgeClass = (jobType) => {
    switch (jobType.toLowerCase()) {
      case 'full-time':
        return 'job-type-badge job-type-fulltime';
      case 'part-time':
        return 'job-type-badge job-type-parttime';
      case 'freelance':
        return 'job-type-badge job-type-freelance';
      case 'contract':
        return 'job-type-badge job-type-contract';
      case 'internship':
        return 'job-type-badge job-type-internship';
      default:
        return 'job-type-badge job-type-default';
    }
  };

  // Application count badge
  const getApplicationCountBadge = (count) => {
    const num = parseInt(count) || 0;
    if (num === 0) return 'app-count-none';
    if (num <= 5) return 'app-count-low';
    if (num <= 20) return 'app-count-medium';
    return 'app-count-high';
  };

  // Days since posted
  const getDaysSincePosted = (dateString) => {
    const posted = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - posted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  // Days until due
  const getDaysUntilDue = (dateString) => {
    const due = new Date(dateString);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: 'Overdue', class: 'due-overdue' };
    if (diffDays === 0) return { text: 'Due Today', class: 'due-today' };
    if (diffDays === 1) return { text: 'Due Tomorrow', class: 'due-tomorrow' };
    if (diffDays <= 7) return { text: `${diffDays} days left`, class: 'due-soon' };
    return { text: `${diffDays} days left`, class: 'due-normal' };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const totalPages = Math.ceil(totalJobs / pageSize);

  return (
    <Dashboard page={"listing"}>
      <div className="admin-job-listing-container">
        <div className="admin-job-listing-content">
          <div className="admin-job-content-header">
            <div className="admin-job-header-info">
              <h1>Job Listing</h1>
              <p className="admin-job-date-range-text">
                Showing {jobs.length} of {totalJobs} jobs
                {getActiveFilterCount() > 0 && ` (${getActiveFilterCount()} filter${getActiveFilterCount() > 1 ? 's' : ''} applied)`}
              </p>
            </div>
            <div className="admin-job-date-range-selector">
              <button 
                className="admin-job-date-range-btn"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                {dateRange.display} 📅
              </button>
              
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <div className="date-picker-header">
                    <h4>Select Date Range</h4>
                    <button 
                      onClick={() => setShowDatePicker(false)}
                      className="close-picker-btn"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="date-picker-presets">
                    <button onClick={() => applyDateRange('today')}>Today</button>
                    <button onClick={() => applyDateRange('yesterday')}>Yesterday</button>
                    <button onClick={() => applyDateRange('last7days')}>Last 7 Days</button>
                    <button onClick={() => applyDateRange('last30days')}>Last 30 Days</button>
                    <button onClick={() => applyDateRange('thisMonth')}>This Month</button>
                  </div>
                  
                  <div className="date-picker-custom">
                    <h5>Custom Range</h5>
                    <div className="date-inputs">
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        className="date-input"
                      />
                      <span>to</span>
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        className="date-input"
                      />
                    </div>
                    <button 
                      onClick={() => handleDateRangeChange(dateRange.startDate, dateRange.endDate)}
                      disabled={!dateRange.startDate || !dateRange.endDate}
                      className="apply-date-btn"
                    >
                      Apply Range
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="admin-job-list-section">
            <div className="admin-job-section-header">
              <h2>Job List</h2>
              <div className="admin-job-header-actions">
                <div className="admin-job-search-container">
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      handleFilterChange('search', e.target.value);
                    }}
                    className="admin-job-search-input"
                  />
                  <span className="admin-job-search-icon">🔍</span>
                </div>
                <button 
                  className="admin-job-filters-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <span className="admin-job-filter-icon">⚙️</span>
                  Filters
                  {getActiveFilterCount() > 0 && (
                    <span className="admin-job-filter-count">{getActiveFilterCount()}</span>
                  )}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="admin-job-filters-panel">
                <div className="admin-job-filters-content">
                  <div className="admin-job-filter-group">
                    <h4>Job Status</h4>
                    <div className="admin-job-filter-options">
                      {['active', 'closed', 'draft', 'paused'].map(status => (
                        <label key={status} className="admin-job-filter-option">
                          <input
                            type="checkbox"
                            checked={filters.status.includes(status)}
                            onChange={(e) => handleFilterChange('status', status, e.target.checked)}
                          />
                          <span className="admin-job-checkmark"></span>
                          <span className="admin-job-option-label">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="admin-job-filter-group">
                    <h4>Job Type</h4>
                    <div className="admin-job-filter-options">
                      {['full-time', 'part-time', 'freelance', 'contract', 'internship'].map(type => (
                        <label key={type} className="admin-job-filter-option">
                          <input
                            type="checkbox"
                            checked={filters.jobType.includes(type)}
                            onChange={(e) => handleFilterChange('jobType', type, e.target.checked)}
                          />
                          <span className="admin-job-checkmark"></span>
                          <span className="admin-job-option-label">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="admin-job-filter-actions">
                    <button onClick={clearAllFilters} className="admin-job-clear-filters-btn">
                      Clear All
                    </button>
                    <button onClick={() => setShowFilters(false)} className="admin-job-apply-filters-btn">
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Active Filters Display */}
            {getActiveFilterCount() > 0 && (
              <div className="active-filters-display">
                <div className="active-filters-header">
                  <span>Active Filters:</span>
                  <button onClick={clearAllFilters} className="clear-all-quick-btn">Clear All</button>
                </div>
                <div className="active-filters-list">
                  {filters.status.map(status => (
                    <span key={status} className="filter-chip">
                      Status: {status.charAt(0).toUpperCase() + status.slice(1)}
                      <button 
                        onClick={() => handleFilterChange('status', status, false)}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {filters.jobType.map(type => (
                    <span key={type} className="filter-chip">
                      Type: {type.charAt(0).toUpperCase() + type.slice(1)}
                      <button 
                        onClick={() => handleFilterChange('jobType', type, false)}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {filters.search && (
                    <span className="filter-chip">
                      Search: "{filters.search}"
                      <button 
                        onClick={() => {
                          setSearchTerm('');
                          handleFilterChange('search', '');
                        }}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.dateFrom && filters.dateTo && (
                    <span className="filter-chip">
                      Date: {dateRange.display}
                      <button 
                        onClick={() => {
                          setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }));
                          setDateRange({ startDate: '', endDate: '', display: 'Select Date Range' });
                        }}
                        className="filter-chip-remove"
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="admin-job-table">
              <div className="table-header">
                <div className="header-cell role-header">Role</div>
                <div className="header-cell company-header">Company</div>
                <div className="header-cell status-header">Status</div>
                <div className="header-cell date-header">Posted</div>
                <div className="header-cell date-header">Due Date</div>
                <div className="header-cell type-header">Type</div>
                <div className="header-cell applicants-header">Applications</div>
              </div>

              <div className="table-body">
                {loading ? (
                  <div className="admin-job-loading-state">Loading jobs...</div>
                ) : jobs.length > 0 ? (
                  jobs.map((job) => {
                    const dueInfo = getDaysUntilDue(job.due_date);
                    return (
                      <div key={job.id} className="table-row" onClick={() => navigate(`/job-detail/${job.id}`)}>
                        <div className="table-cell role-cell">
                          <div className="job-role-info">
                            <span className="job-role-title">{job.title}</span>
                            <span className="job-posted-time">{getDaysSincePosted(job.created_at)}</span>
                          </div>
                        </div>
                        <div className="table-cell company-cell">
                          <span className="company-name">{job.company_name}</span>
                        </div>
                        <div className="table-cell status-cell">
                          <span className={getStatusBadgeClass(job.status)}>
                            {job.status}
                          </span>
                        </div>
                        <div className="table-cell date-cell">
                          <span className="formatted-date">{formatDate(job.created_at)}</span>
                        </div>
                        <div className="table-cell date-cell">
                          <div className="due-date-info">
                            <span className="formatted-date">{formatDate(job.due_date)}</span>
                            <span className={`due-indicator ${dueInfo.class}`}>{dueInfo.text}</span>
                          </div>
                        </div>
                        <div className="table-cell type-cell">
                          <span className={getJobTypeBadgeClass(job.job_type)}>
                            {job.job_type}
                          </span>
                        </div>
                        <div className="table-cell applicants-cell">
                          <div className="application-info">
                            <span className={`application-count ${getApplicationCountBadge(job.application_counts)}`}>
                              {job.application_counts.toLocaleString()}
                            </span>
                            <span className="application-label">applications</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="admin-job-empty-state">
                    No jobs found matching your filters.
                    <button onClick={clearAllFilters} className="clear-filters-suggestion">
                      Clear filters to see all jobs
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-job-pagination">
              <div className="admin-job-pagination-info">
                <span>View</span>
                <select 
                  value={pageSize} 
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="admin-job-page-size-select"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>Jobs per page</span>
              </div>
              
              <div className="admin-job-pagination-controls">
                <button 
                  className="admin-job-page-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`admin-job-page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                
                <button 
                  className="admin-job-page-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="admin-job-error-message">
            {error}
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default AdminJobListing;